import { UserSession } from '@app/services/user-session.service';
import * as _ from 'lodash';

// Import internal
import { Cell } from './cell.model';
import { DEFAULT_VALUES, DefaultGdpr } from '@modules/projects/gdpr.data';
import { CustomerDetail } from './accounting-sage.model';
import {
    GdprRaw,
    Company,
    SaasHttp,
    Processing,
    Office,
    ContractHolder,
    CloudPartner,
    DataTransferAuthorizationTemplate,
    AgreementType,
    Entity,
    DataTransferAuthorization,
    DataTransferEntity,
    Saas,
} from './gdpr.model';
import { Embedded, EmbeddedUser, toUserEmbedded } from './embed.model';
import { FormBuilder, FormGroup } from '@angular/forms';

export class Gdpr extends GdprRaw {

    constructor(data?: GdprRaw) {
        super();
        this.project = data?.project;
        this.status = data?.status;
        this.company = data?.company;
        this.processings = data?.processings;
        this.offices = data?.offices;
        this.excludedOffices = data?.excludedOffices;
        this.datacenters = data?.datacenters;
        this.cloudPartners = data?.cloudPartners;
        this.saas = data?.saas;
        this.excludedSaas = data?.excludedSaas;
        this.createdAt = data?.createdAt;
        this.createdBy = data?.createdBy;
        this.updatedAt = data?.updatedAt;
        this.updatedBy = data?.updatedBy;
        this.declaredAt = data?.declaredAt;
        this.declaredBy = data?.declaredBy;
        this.dataTransferAuthorizations = data?.dataTransferAuthorizations;
        this.subcontracting = data?.subcontracting;
    }

    public static toCustomGdpr<T extends embedGdpr>(data: Gdpr, embedded: Embedded, customFields: T[]): EmbedGdpr<T> {
        const customData = customFields.reduce((acc, field: embedGdpr) => {
            acc[field] = gdprConfiguration[field].transform(data, embedded);
            return acc;
        }, {});

        // TODO (hacky)  metadata has to be convert to map...
        return { ...new Gdpr(data), ...customData } as EmbedGdpr<T>;
    }

    public static toDataTransferAuthorization(data: DataTransferAuthorizationTemplate): DataTransferAuthorization {
        return {
            agreementType: data.agreementType,
            authorizedLocation: data.authorizedLocation,
        };
    }

    public static toOffice(data: Office): Office {
        return {
            name: data.name,
            address: data.address,
            countryCodes: data.countryCodes,
            legalGuarantees: data.legalGuarantees,
            proofLink: data.proofLink,
            declaredAt: data.declaredAt,
            declaredBy: data.declaredBy,
            effectiveAt: data.effectiveAt,
        };
    }

    public static toSaas(data: Saas): SaasHttp {
        return {
            name: data.name,
            address: data.address,
            countryCodes: data.countryCodes,
            legalGuarantees: data.legalGuarantees,
            solution: data.solution,
            proofLink: data.proofLink,
            declaredAt: data.declaredAt,
            declaredBy: data.declaredBy,
            effectiveAt: data.effectiveAt,
        };
    }

    public static toExcludedOffice(data: Office): Office {
        return {
            name: data.name,
            address: data.address,
            countryCodes: data.countryCodes,
        };
    }

    public static toExcludedSaas(data: Saas): SaasHttp {
        return {
            name: data.name,
            solution: data.solution,
            address: data.address,
            countryCodes: data.countryCodes,
        };
    }

    public static fromRaw(raw: GdprRaw): Gdpr {
        return new Gdpr(raw);
    }

    public static getSaasFormGroup(
        saas: SaasHttp,
        formBuilder: FormBuilder,
        userSession: UserSession,
        dataTransfer?: DataTransferAuthorization): FormGroup {
        const formDataTransfer = this.getServiceOffersDataTransferFromGroup(saas, formBuilder, userSession, dataTransfer);
        return formBuilder.group({
            name: [saas.name],
            solution: [saas.solution],
            countryCodes: formBuilder.array(saas.countryCodes || []),
            address: [saas.address],
            ...formDataTransfer.controls,
        });
    }

    public static getOfficesFormGroup(
        entity: Office,
        formBuilder: FormBuilder,
        userSession: UserSession,
        dataTransfer: DataTransferAuthorization): FormGroup {
        const form = this.getEntityFormGroup(entity, formBuilder);
        const formDataTransfer = this.getServiceOffersDataTransferFromGroup(entity, formBuilder, userSession, dataTransfer);

        return formBuilder.group({
            ...form.controls,
            ...formDataTransfer.controls,
        });
    }

    public static getEntityFormGroup(entity: Entity, formBuilder: FormBuilder): FormGroup {
        return formBuilder.group({
            name: [entity.name],
            address: [entity.address],
            countryCodes: formBuilder.array(entity.countryCodes || []),
        });
    }

    public static getCloudPartnerFormGroup(cloudPartner: CloudPartner, formBuilder: FormBuilder): FormGroup {
        return formBuilder.group({
            name: [cloudPartner.name],
            address: [cloudPartner.address],
            countryCodes: formBuilder.array(cloudPartner.countryCodes || []),
            contractHolder: [cloudPartner.contractHolder || ContractHolder.CLARANET],
        });
    }

    public static getProcessingFormGroup(processing: Processing, formBuilder: FormBuilder): FormGroup {
        return formBuilder.group({
            name: [processing.name],
            solution: [processing.solution],
            dataCategories: [processing.dataCategories],
            peopleCategories: [processing.peopleCategories],
            hasHealthData: [processing.hasHealthData],
        });
    }

    public static getDataTransferAuthorizationFormGroup(dataTransferAuthorization: DataTransferAuthorizationTemplate, formBuilder: FormBuilder): FormGroup {
        return formBuilder.group({
            authorizedLocation: [dataTransferAuthorization.authorizedLocation],
            agreementType: [dataTransferAuthorization.agreementType, (control) => {
                const isUndeclared = control.value === AgreementType.UNDECLARED;
                return isUndeclared ? { undeclaredInvalid: true } : undefined;
            }],
            allowedCountry: [dataTransferAuthorization.allowedCountry],
            isFr: [dataTransferAuthorization.isFr],
        });
    }

    private static getServiceOffersDataTransferFromGroup(
        entity: DataTransferEntity,
        formBuilder: FormBuilder,
        userSession: UserSession,
        dataTransfer?: DataTransferAuthorization): FormGroup {
        return formBuilder.group({
            proofLink: [entity.proofLink ?? ''],
            declaredAt: [entity.declaredAt ? new Date(entity.declaredAt) : new Date()],
            declaredBy: [entity.declaredBy || userSession.id],
            effectiveAt: [entity.effectiveAt ? new Date(entity.effectiveAt) : new Date()],
            dataTransfer: [dataTransfer]
        });
    }

    public setFromFormGroup(formGroup: any): void {
        for (const prop in formGroup) {
            if (this.hasOwnProperty(prop)) {
                this[prop] = formGroup[prop];
            }
        }
    }

    /**
     * Set offer only updates object tags used to fill appropriate GDPR data (datacenters, saas, cloud partner...)
     * The tags field is used here only for ui convenience
     */
    public setOffer(cell: Cell): void {
        this.tags = cell.tags;
    }

    public setCompany(customer: CustomerDetail): void {
        const company: Company = {};

        if (!customer) {
            this.company = company;
        }

        company.accountingId = customer.accountingId;
        company.name = customer.name;
        if (_.isEmpty(customer.address2)) {
            company.address = `${customer.address1} ${customer.postalCode} ${customer.city}`;
        } else {
            company.address = `${customer.address1} ${customer.address2} ${customer.postalCode} ${customer.city}`;
        }

        if (!_.isEmpty(customer.phoneNumber)) {
            company.phoneNumber = customer.phoneNumber;
        }

        this.company = company;
    }

    public updateOffer(cell?: Cell): void {
        const cellTags = cell?.tags.map(tag => tag.toLowerCase()) || [];
        if (cellTags.includes('template_rgpd:cba')) {
            this.updateGdprInfos(DEFAULT_VALUES['template_rgpd:cba']);
        } else if (cellTags.includes('template_rgpd:esante')) {
            this.updateGdprInfos(DEFAULT_VALUES['template_rgpd:esante']);
        } else if (cellTags.includes('template_rgpd:data')) {
            this.updateGdprInfos(DEFAULT_VALUES['template_rgpd:data']);
        } else if (cellTags.includes('template_rgpd:webapps')) {
            this.updateGdprInfos(DEFAULT_VALUES['template_rgpd:webapps']);
        } else {
            this.updateGdprInfos(DEFAULT_VALUES['template_rgpd:default']);
        }

        this.tags = cell?.tags || [];
    }

    private updateGdprInfos(infos: DefaultGdpr): void {
        this.offices = infos.offices;
        this.datacenters = infos.datacenters;
        this.cloudPartners = infos.cloudPartners;
        this.saas = infos.saas;
        this.dataTransferAuthorizations = infos.dataTransferAuthorizations;
    }

}

export type embedGdpr = keyof EmbedProperties;

export interface EmbedProperties {
    $createdBy: EmbeddedUser;
    $updatedBy: EmbeddedUser;
    $deletedBy: EmbeddedUser;
    $declaredBy: EmbeddedUser;
}

export type EmbedGdpr<T extends embedGdpr> = Gdpr & Partial<Pick<EmbedProperties, T>>;

export const gdprConfiguration: { [K in embedGdpr]: { embed: string; transform: (gdpr: Gdpr, embedded: Embedded) => EmbedProperties[K] } } = {
    $createdBy: {
        embed: 'users',
        transform: (data: GdprRaw, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'createdBy', embedded),
    },
    $updatedBy: {
        embed: 'users',
        transform: (data: GdprRaw, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'updatedBy', embedded),
    },
    $deletedBy: {
        embed: 'users',
        transform: (data: GdprRaw, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'deletedBy', embedded),
    },
    $declaredBy: {
        embed: 'users',
        transform: (data: GdprRaw, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'declaredBy', embedded),
    },
};
